import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Lists.module.css';
import RingBufferView from '../listengine/RingBufferEngine';
import { ListCommand } from '../../../types/ListCommands';

const BUFFER_SIZE = 10;

const RING_BUFFER_CODE = `class RingBuffer {
  constructor(capacity) {
    this.buffer = new Array(capacity).fill(null);
    this.head = 0;
    this.tail = 0; 
    this.size = 0;
  }

  add(value) {
    this.buffer[this.tail] = value;
    this.tail = (this.tail + 1) % this.capacity;
  }
    
  delete() {
    const value = this.buffer[this.head];
    this.buffer[this.head] = null;
    this.head = (this.head + 1) % this.capacity;
  }
}`;

const RingBufferVisualizer: React.FC = () => {
  const navigate = useNavigate();
  const [command, setCommand] = useState<ListCommand | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeLine, setActiveLine] = useState<number | null>(null);

const animationResolver: React.MutableRefObject<((value?: void) => void) | null> = useRef(null);

  const handleAnimationComplete = () => {
    if (animationResolver.current) {
      animationResolver.current();
      animationResolver.current = null;
    } else {
      setIsAnimating(false);
    }
    setActiveLine(null);
  };

  const handleEnqueue = () => {
    if (isAnimating || inputValue === '') return;
    setIsAnimating(true);
    setActiveLine(9);
    setCommand({ id: Date.now(), action: 'addLast', value: parseInt(inputValue) });
    setInputValue('');
  };

  const handleDequeue = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveLine(15);
    setCommand({ id: Date.now(), action: 'removeFirst' });
  };
  
  const handleEnqueueRandom = async () => {
    if (isAnimating) return;
    setIsAnimating(true);
    const numbersToAdd = Math.floor(Math.random() * 3) + 2;

    for (let i = 0; i < numbersToAdd; i++) {
      await new Promise(resolve => {
          animationResolver.current = resolve;
          setActiveLine(9);
          const randomValue = Math.floor(Math.random() * 100);
          setCommand({ id: Date.now(), action: 'addLast', value: randomValue });
      });
    }
    setIsAnimating(false);
  };
  
  const handleDequeueMultiple = async () => {
    if (isAnimating) return;
    setIsAnimating(true);
    const numbersToRemove = 2;

    for (let i = 0; i < numbersToRemove; i++) {
        await new Promise(resolve => {
            animationResolver.current = resolve;
            setActiveLine(15);
            setCommand({ id: Date.now(), action: 'removeFirst' });
        });
    }
    setIsAnimating(false);
  };

  const handleReset = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCommand({ id: Date.now(), action: 'reset' });
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>
     <h1 className={styles.title}>Ring Buffer</h1>
      <div className={styles.description}>
           <h2>Description</h2>
           <p>A Ring Buffer (or Circular Buffer) is a fixed-size data structure that uses a single, fixed-size buffer as if it were connected end-to-end. When the buffer is full, new data overwrites the oldest data.</p>
        </div>
        <div className={styles.useCase}>
           <h2>Use Cases</h2>
           <p>Perfect for streaming data like audio or video packets, handling data from I/O devices, or creating caches that only store the most recent N items. Many logging systems use them internally.</p>
        </div>
        <div className={styles.timeComplexity}>
           <h2>Time Complexity</h2>
           <p>The primary advantage of a Ring Buffer is its speed. Both enqueue (adding) and dequeue (removing) operations are extremely fast, always taking <strong>O(1)</strong>, or constant time, because no data ever needs to be shifted.</p>
        </div>
      <div className={styles.mainContent}>
        <div className={styles.visualization}>
          <div style={{flex: 1}}>
            <RingBufferView command={command} onComplete={handleAnimationComplete} />
          </div>
        </div> 
        <div className={styles.controlsAndCode}>
            <div className={styles.controls}>
                <input type="number" value={inputValue} onChange={(e) => setInputValue(e.target.value)} placeholder="Value" disabled={isAnimating} />
                <button onClick={handleEnqueue} disabled={isAnimating}>Add</button>
                <button onClick={handleDequeue} disabled={isAnimating}>Delete</button>
                <button onClick={handleEnqueueRandom} disabled={isAnimating}>Add Random </button>
                <button onClick={handleDequeueMultiple} disabled={isAnimating}>Delete x2</button>
                <button onClick={handleReset} disabled={isAnimating}>Reset</button>
            </div>
            
              <div className={styles.codeDisplay}>
                {RING_BUFFER_CODE.split('\n').map((line, index) => (
                <div key={index} className={`${styles.codeLine} ${activeLine === index ? styles.highlightedCode : ''}`}>
                  {line || ' '}
              </div>
              ))}
              </div>
        </div>
      </div>
    </div>
  );
};

export default RingBufferVisualizer;